import { Warning } from "@mui/icons-material";
import InfoIcon from "@mui/icons-material/Info";
import { Button, Tooltip } from "@mui/material";
import cx from "classnames";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import { ReactComponent as XMark } from "assets/x-mark.svg";
import { estimateInvoiceTotal } from "components/Payment/UpgradeFlowComponents/BillingTab";
import {
  SLAPSHOT_MY_PLAN_URL,
  SLAPSHOT_MY_PLAN_UPGRADE_URL,
  SLAPSHOT_SUBSCRIPTION_UPGRADE_URL,
  SLAPSHOT_SUBSCRIPTION_URL,
} from "constants/urls";
import { UserContext } from "layouts/SlapshotMainLayout";
import { Project } from "models/Project";
import { Subscription } from "models/User";
import {
  formatPrice,
  formatSize,
  getDaysFromToday,
  getSingularOrPlural,
} from "utils/helper";

import styles from "./Usage.module.scss";

interface UsageIndicatorProps {
  used: number;
  limit: number;
}

export const UsageIndicator: React.FC<UsageIndicatorProps> = ({
  used,
  limit,
}) => {
  const [usage, setUsage] = useState(0);

  const getUsage = useCallback(() => {
    if (limit === 0) return 0;

    return Math.min(100, Math.max(0, (used / limit) * 100));
  }, [limit, used]);

  const getColor = (value) => {
    const colors = [
      { value: 0, color: [19, 176, 35] },
      { value: 15, color: [19, 176, 35] },
      { value: 55, color: [255, 199, 95] },
      { value: 85, color: [255, 69, 69] },
      { value: 100, color: [255, 69, 69] },
    ];

    const startColor =
      colors.find((c, i) => value <= colors[i + 1]?.value) || colors[0];
    const endColor =
      colors.find((c) => value <= c.value) || colors[colors.length - 1];

    const range = endColor.value - startColor.value;
    const factor = range === 0 ? 0 : (value - startColor.value) / range;

    const r = Math.round(
      startColor.color[0] + factor * (endColor.color[0] - startColor.color[0]),
    );
    const g = Math.round(
      startColor.color[1] + factor * (endColor.color[1] - startColor.color[1]),
    );
    const b = Math.round(
      startColor.color[2] + factor * (endColor.color[2] - startColor.color[2]),
    );

    return `rgba(${r}, ${g}, ${b}, 1)`;
  };

  useEffect(() => {
    setUsage(getUsage);
  }, [getUsage]);

  return (
    <div className={styles.indicatorCon}>
      <div
        className={styles.indicator}
        style={{ backgroundColor: getColor(usage), width: `${usage}%` }}
      />
    </div>
  );
};

interface UsageProps {
  subscription: Subscription | null;
  project?: Project;
}

const Usage: React.FC<UsageProps> = ({ subscription, project }) => {
  const navigate = useNavigate();
  const estimatedCost = useMemo(() => {
    if (!subscription || subscription.frames_used <= subscription.frames_limit)
      return null;
    return {
      currency: subscription.plan.price.currency,
      unit_amount_decimal: estimateInvoiceTotal(subscription),
    };
  }, [subscription]);

  return (
    <div>
      {subscription && !subscription.is_free_trial_period && (
        <Tooltip
          placement="bottom-end"
          arrow
          slotProps={{
            tooltip: {
              sx: {
                bgcolor: "transparent",
                marginTop: "0px !important",
                "& .MuiTooltip-arrow": {
                  color: "#2C2C2C",
                  fontSize: "large",
                  top: "4px !important",
                  left: "-30px !important",
                },
              },
            },
          }}
          title={
            <div className={styles.container}>
              <div className={styles.header}>
                <div className={styles.heading}>
                  {subscription.plan.name} plan
                </div>
                {/* <XMark /> */}
              </div>
              <div className={styles.body}>
                <div className={styles.stat}>
                  <div className={styles.statDetail}>
                    <div className={styles.statName}>Asset Storage</div>
                    <div className={styles.statText}>
                      <span className={styles.primaryValue}>
                        {formatSize(subscription.storage_used)}
                      </span>
                      <span>{` of ${formatSize(subscription.plan.storage, "GB")}`}</span>
                    </div>
                  </div>
                  <div className={styles.usageIndicatorCon}>
                    <UsageIndicator
                      used={subscription.storage_used}
                      limit={subscription.plan.storage * 1024 ** 3}
                    />
                  </div>
                </div>

                <div className={styles.stat}>
                  <div className={styles.statDetail}>
                    <div className={styles.statName}>Exported Frames</div>
                    <div className={styles.statText}>
                      <span className={styles.primaryValue}>
                        {subscription.frames_used}
                      </span>
                      <span>{` of ${subscription.frames_limit}`}</span>
                    </div>
                  </div>
                  <div className={styles.usageIndicatorCon}>
                    <UsageIndicator
                      used={subscription.frames_used}
                      limit={subscription.frames_limit}
                    />
                  </div>
                  {subscription.renewal_date && (
                    <div className={styles.resetIn}>
                      Export quota will be reset in{" "}
                      {getDaysFromToday(subscription.renewal_date)}{" "}
                      {getSingularOrPlural(
                        getDaysFromToday(subscription.renewal_date),
                        "day",
                      )}
                      .
                    </div>
                  )}
                </div>

                <div className={styles.stat}>
                  <div className={styles.statDetail}>
                    <div className={styles.statName}>
                      Autopilot Exported Frames
                    </div>
                    <div className={styles.statText}>
                      <span className={styles.primaryValue}>
                        {subscription.autopilot_frames_used}
                      </span>
                      <span>{` of ${subscription.autopilot_frames_limit}`}</span>
                    </div>
                  </div>
                  <div className={styles.usageIndicatorCon}>
                    <UsageIndicator
                      used={subscription.autopilot_frames_used}
                      limit={subscription.autopilot_frames_limit}
                    />
                  </div>
                  {subscription.renewal_date && (
                    <div className={styles.resetIn}>
                      Autopilot export quota will be reset in{" "}
                      {getDaysFromToday(subscription.renewal_date)}{" "}
                      {getSingularOrPlural(
                        getDaysFromToday(subscription.renewal_date),
                        "day",
                      )}
                      .
                    </div>
                  )}
                </div>

                {project?.company.is_admin && estimatedCost && (
                  <div className={styles.estimatedCost}>
                    <Warning className={styles.warningIcon} />
                    <div>
                      Your estimated cost for this month is{" "}
                      <span>{formatPrice(estimatedCost)}</span>
                    </div>
                  </div>
                )}
              </div>
              {project?.company.is_admin && (
                <div className={styles.footer}>
                  <Button
                    className={cx(styles.noBackgroundButton, styles.usageBtn)}
                    onClick={() =>
                      navigate(
                        project.company.is_owner
                          ? `${SLAPSHOT_MY_PLAN_URL}?tab=usage`
                          : `${SLAPSHOT_SUBSCRIPTION_URL}?workspaceId=${project.company.id}&workspaceName=${project.company.name}&tab=usage`,
                      )
                    }
                  >
                    View usage
                  </Button>
                  <Button
                    className={styles.upgrade}
                    onClick={() =>
                      navigate(
                        project.company.is_owner
                          ? SLAPSHOT_MY_PLAN_URL
                          : `${SLAPSHOT_SUBSCRIPTION_URL}?workspaceId=${project.company.id}&workspaceName=${project.company.name}`,
                      )
                    }
                  >
                    Manage plan
                  </Button>
                </div>
              )}
            </div>
          }
        >
          <div className={styles.utCon}>
            <div className={styles.utIndicator}>
              <UsageIndicator
                used={subscription.frames_used}
                limit={subscription.frames_limit}
              />
            </div>
            <div className={styles.utDetail}>
              <span className={styles.utPrimary}>
                {subscription.frames_used}
              </span>
              <span>{` / ${subscription.frames_limit}`}</span>
              <span style={{ fontStyle: "italic" }}> frames exported</span>
            </div>
            <InfoIcon className={styles.infoIcon} />
          </div>
        </Tooltip>
      )}
    </div>
  );
};

export default Usage;
